import React, { useEffect, useState } from 'react';
import { UserCheck, TrendingUp, Rocket, Headphones, Megaphone, GraduationCap, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import AnimatedSection from '../components/AnimatedSection';
import { partnerBenefits } from '../mockData';
import { trackButtonClick, trackFormSubmit, trackPageView } from '../utils/analytics';
import { submitFormToLambda } from '../utils/api';
import { toast as sonnerToast } from 'sonner';

const PartnerProgram = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    gst: '',
    region: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    trackPageView('/partner-program', 'Partner Program');
  }, []);

  const iconMap = {
    UserCheck,
    TrendingUp,
    Rocket,
    Headphones,
    Megaphone,
    GraduationCap
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission
    trackFormSubmit('partner_program', { company: formData.company, region: formData.region });

    try {
      // Submit to Lambda API
      await submitFormToLambda('partner_program', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        gst: formData.gst,
        region: formData.region,
        comments: formData.comments
      });

      toast({
        title: 'Application Submitted!',
        description: 'Thank you for your interest. Our team will contact you within 24 hours.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        gst: '',
        region: '',
        comments: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      sonnerToast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Partner With TallyBharat
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Join India's leading Tally cloud hosting network and grow your business with attractive margins and dedicated support.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Partner Benefits
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Everything you need to succeed as a TallyBharat partner
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnerBenefits.map((benefit, index) => {
              const Icon = iconMap[benefit.icon];
              return (
                <AnimatedSection key={index} animation="scaleIn" delay={index * 100}>
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                    <div className="bg-gradient-to-br from-[#0056D2] to-[#0A1F44] w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      {Icon && <Icon size={28} className="text-white" />}
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A1F44] mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  Why Partner with TallyBharat?
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  As an authorized Tally partner since 2004, we've built a robust network of successful partners across India. Join us to leverage our experience, infrastructure, and support.
                </p>
                <div className="space-y-4 pt-4">
                  {[
                    '19+ years of industry expertise',
                    'Proven track record with 2000+ clients',
                    'Comprehensive training and onboarding',
                    'Co-branded marketing materials',
                    'Recurring revenue model',
                    '24/7 technical support for your clients'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle2 className="text-[#0056D2] flex-shrink-0" size={22} />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1000&h=1000&fit=crop"
                    alt="Partnership"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#FF7A00] opacity-20 rounded-3xl -z-10" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Partner Application Form */}
      <section className="py-24 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Join Our Partner Network
              </h2>
              <p className="text-xl text-gray-600">
                Fill out the form below and our team will get back to you within 24 hours
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="scaleIn" delay={200}>
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                    placeholder="+91 98XXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                    Company Name *
                  </label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                    placeholder="Your Company Ltd."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                    GST Number
                  </label>
                  <Input
                    type="text"
                    name="gst"
                    value={formData.gst}
                    onChange={handleChange}
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                    Region/State *
                  </label>
                  <Input
                    type="text"
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                    placeholder="Rajasthan"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-[#0A1F44] mb-2">
                  Additional Comments
                </label>
                <Textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  rows={4}
                  className="w-full rounded-lg border-gray-300 focus:border-[#0056D2] focus:ring-[#0056D2]"
                  placeholder="Tell us about your business and why you'd like to partner with us..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                onClick={() => trackButtonClick('submit_partner_form', 'partner_form')}
                className="w-full mt-8 bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold py-6 text-lg rounded-full transition-all duration-200 hover:scale-105"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default PartnerProgram;